import type { A2uiComponentEntry, SpaceDocument, SpaceMetadata, SpaceSettings, SpaceWire } from 'types/space';
import type { SpaceHostMessage } from '../composition-host/types';
import type { GridPosition } from './gridPlacement';

/**
 * A component's session-scoped context/view lens for Try mode — never part of the draft
 * `SpaceDocument` until explicitly promoted (`applyOverrideToDocument`). Stored on the a2ui
 * component entry via `setContextViewBinding` once promoted (EAINT-844 Phase 2).
 */
export interface ContextViewBinding {
  contextSystemName?: string;
  viewSystemName?: string;
}

/** One component's session-override buffer entry. Never persisted — see `serializeForSave`. */
export interface ComposerOverride extends ContextViewBinding {
  /** Scratch `updateDataModel`-shaped patch, scoped to this component's preview only. */
  dataModelPatch?: Record<string, unknown>;
}

/** A proposed set of components (e.g. from the composer agent) not yet in the draft. */
export interface GhostProposal {
  id: string;
  components: A2uiComponentEntry[];
}

export interface ComposerState {
  draft: SpaceDocument;
  /** Keyed by component id. Session-only — assembled into render input, never serialized. */
  overrides: Record<string, ComposerOverride>;
  ghosts: GhostProposal[];
  /** True once the draft has any edit applied since `createComposerState`. */
  dirty: boolean;
}

export function createComposerState(draft: SpaceDocument): ComposerState {
  return { draft, overrides: {}, ghosts: [], dirty: false };
}

// ============================================================
// Draft edit reducer
// ============================================================

export type ComposerEditAction =
  | { type: 'placeComponent'; entry: A2uiComponentEntry }
  | { type: 'removeComponent'; componentId: string }
  | { type: 'setProps'; componentId: string; props: Record<string, unknown> }
  | { type: 'setGridPosition'; componentId: string; gridPosition: GridPosition }
  | { type: 'addWire'; wire: SpaceWire }
  | { type: 'removeWire'; wireId: string }
  | { type: 'setContextViewBinding'; componentId: string; binding: ContextViewBinding }
  | { type: 'replaceSurface'; component: string }
  | { type: 'setSettings'; patch: Partial<SpaceSettings> }
  | { type: 'setMetadata'; patch: Partial<SpaceMetadata> };

function upsertComponent(components: A2uiComponentEntry[], entry: A2uiComponentEntry): A2uiComponentEntry[] {
  const idx = components.findIndex((c) => c.id === entry.id);
  if (idx === -1) return [...components, entry];
  const next = [...components];
  next[idx] = entry;
  return next;
}

/** No-op (same array reference returned) when `componentId` doesn't exist — lets callers detect a dead edit. */
function patchComponent(
  components: A2uiComponentEntry[],
  componentId: string,
  patch: Record<string, unknown>
): A2uiComponentEntry[] {
  const idx = components.findIndex((c) => c.id === componentId);
  if (idx === -1) return components;
  const next = [...components];
  next[idx] = { ...next[idx], ...patch };
  return next;
}

/**
 * Applies one edit to the draft `SpaceDocument`. Pure — returns the SAME `draft` reference when
 * the edit is a no-op (e.g. patching a component id that doesn't exist), so `composerEditReducer`
 * can tell a real edit from a dead one without a separate diff.
 */
function applyEditToDraft(draft: SpaceDocument, action: ComposerEditAction): SpaceDocument {
  switch (action.type) {
    case 'placeComponent': {
      const components = upsertComponent(draft.a2ui.components, action.entry);
      return { ...draft, a2ui: { ...draft.a2ui, components } };
    }
    case 'removeComponent': {
      const components = draft.a2ui.components.filter((c) => c.id !== action.componentId);
      if (components.length === draft.a2ui.components.length) return draft;
      return { ...draft, a2ui: { ...draft.a2ui, components } };
    }
    case 'setProps': {
      const components = patchComponent(draft.a2ui.components, action.componentId, action.props);
      if (components === draft.a2ui.components) return draft;
      return { ...draft, a2ui: { ...draft.a2ui, components } };
    }
    case 'setGridPosition': {
      const components = patchComponent(draft.a2ui.components, action.componentId, { gridPosition: action.gridPosition });
      if (components === draft.a2ui.components) return draft;
      return { ...draft, a2ui: { ...draft.a2ui, components } };
    }
    case 'addWire': {
      return { ...draft, wires: [...(draft.wires ?? []), action.wire] };
    }
    case 'removeWire': {
      const wires = (draft.wires ?? []).filter((w) => w.id !== action.wireId);
      if (wires.length === (draft.wires ?? []).length) return draft;
      return { ...draft, wires };
    }
    case 'setContextViewBinding': {
      const components = patchComponent(draft.a2ui.components, action.componentId, { contextViewBinding: action.binding });
      if (components === draft.a2ui.components) return draft;
      return { ...draft, a2ui: { ...draft.a2ui, components } };
    }
    case 'replaceSurface': {
      // A full replace, not a patch (EAINT-844 Phase F4): the outgoing surface's type-specific
      // props (e.g. a `SpaceGrid`'s `columns`) don't apply to the incoming family and would be
      // stale configuration if merged. The id (and therefore `surface.componentId`/`a2ui.root`,
      // and anything wired to it) is kept, so only the component's own identity swaps.
      const idx = draft.a2ui.components.findIndex((c) => c.id === draft.surface.componentId);
      if (idx === -1 || draft.a2ui.components[idx].component === action.component) return draft;
      const components = [...draft.a2ui.components];
      components[idx] = { id: components[idx].id, component: action.component };
      return { ...draft, surface: { ...draft.surface, family: action.component }, a2ui: { ...draft.a2ui, components } };
    }
    case 'setSettings': {
      return { ...draft, settings: { ...(draft.settings ?? {}), ...action.patch } };
    }
    case 'setMetadata': {
      return { ...draft, metadata: { ...(draft.metadata ?? {}), ...action.patch } };
    }
    default:
      return draft;
  }
}

/** Applies one edit action to the whole composer state, flipping `dirty` only on a real change. */
export function composerEditReducer(state: ComposerState, action: ComposerEditAction): ComposerState {
  const draft = applyEditToDraft(state.draft, action);
  if (draft === state.draft) return state;
  return { ...state, draft, dirty: true };
}

// ============================================================
// Session-override ops
// ============================================================

/** Sets (merges onto any existing entry) a component's session-only context/view override. */
export function setContextViewOverride(state: ComposerState, componentId: string, binding: ContextViewBinding): ComposerState {
  const existing = state.overrides[componentId] ?? {};
  return {
    ...state,
    overrides: { ...state.overrides, [componentId]: { ...existing, ...binding } },
  };
}

/** Clears only the context/view half of a component's override, keeping any scratch data-model patch. */
export function clearContextViewOverride(state: ComposerState, componentId: string): ComposerState {
  const existing = state.overrides[componentId];
  if (!existing) return state;
  const { dataModelPatch } = existing;
  const overrides = { ...state.overrides };
  if (dataModelPatch) {
    overrides[componentId] = { dataModelPatch };
  } else {
    delete overrides[componentId];
  }
  return { ...state, overrides };
}

/** Merges a scratch (session-only) data-model patch onto a component's override entry. */
export function setScratchDataModel(state: ComposerState, componentId: string, patch: Record<string, unknown>): ComposerState {
  const existing = state.overrides[componentId] ?? {};
  return {
    ...state,
    overrides: {
      ...state.overrides,
      [componentId]: { ...existing, dataModelPatch: { ...(existing.dataModelPatch ?? {}), ...patch } },
    },
  };
}

/** Drops the entire session-override buffer (all components), never touching the draft. */
export function clearAllOverrides(state: ComposerState): ComposerState {
  if (Object.keys(state.overrides).length === 0) return state;
  return { ...state, overrides: {} };
}

/**
 * Promotes a component's session override into a real draft edit (`setContextViewBinding`) and
 * clears that override buffer entry — the override→draft handoff SC4 pins.
 */
export function applyOverrideToDocument(state: ComposerState, componentId: string): ComposerState {
  const override = state.overrides[componentId];
  if (!override) return state;

  let next = state;
  if (override.contextSystemName !== undefined || override.viewSystemName !== undefined) {
    next = composerEditReducer(next, {
      type: 'setContextViewBinding',
      componentId,
      binding: { contextSystemName: override.contextSystemName, viewSystemName: override.viewSystemName },
    });
  }

  const overrides = { ...next.overrides };
  delete overrides[componentId];
  return { ...next, overrides };
}

// ============================================================
// Ghost buffer ops
// ============================================================

/** Adds a ghost proposal to the buffer. Does not touch the draft. */
export function proposeGhost(state: ComposerState, ghost: GhostProposal): ComposerState {
  return { ...state, ghosts: [...state.ghosts, ghost] };
}

/** Rebases an accepted ghost's components into the draft (upsert by id) and removes it from the buffer. */
export function acceptGhost(state: ComposerState, ghostId: string): ComposerState {
  const ghost = state.ghosts.find((g) => g.id === ghostId);
  if (!ghost) return state;

  const components = ghost.components.reduce((acc, entry) => upsertComponent(acc, entry), state.draft.a2ui.components);
  const draft: SpaceDocument = { ...state.draft, a2ui: { ...state.draft.a2ui, components } };

  return { ...state, draft, dirty: true, ghosts: state.ghosts.filter((g) => g.id !== ghostId) };
}

/** Discards a rejected ghost. The draft is left untouched. */
export function rejectGhost(state: ComposerState, ghostId: string): ComposerState {
  if (!state.ghosts.some((g) => g.id === ghostId)) return state;
  return { ...state, ghosts: state.ghosts.filter((g) => g.id !== ghostId) };
}

// ============================================================
// Render-input assembly + save serialization
// ============================================================

/**
 * Assembles the `SpaceHostMessage[]` a `CompositionHost` renders: `[createSurface(draft)]` first
 * (so the override/ghost layers below always render AFTER the draft they overlay — SC3), then one
 * `updateDataModel` carrying the whole session-override buffer (if non-empty) keyed under
 * `__composerOverrides`, then one `updateComponents` per pending ghost proposal.
 */
export function assembleRenderInput(state: ComposerState, surfaceId: string): SpaceHostMessage[] {
  const messages: SpaceHostMessage[] = [
    {
      type: 'createSurface',
      surfaceId,
      components: state.draft.a2ui.components,
      root: state.draft.a2ui.root,
      dataModel: state.draft.a2ui.dataModel,
    },
  ];

  if (Object.keys(state.overrides).length > 0) {
    messages.push({
      type: 'updateDataModel',
      surfaceId,
      patch: { __composerOverrides: state.overrides },
    });
  }

  state.ghosts.forEach((ghost) => {
    messages.push({ type: 'updateComponents', surfaceId, components: ghost.components });
  });

  return messages;
}

/**
 * The document persisted on save — the draft, verbatim. Overrides and ghosts live only in
 * `ComposerState` and are never merged in, so a session override can never leak into a save
 * (SC3, SC4).
 */
export function serializeForSave(state: ComposerState): SpaceDocument {
  return state.draft;
}
